import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { ToastProvider } from './context/ToastContext';
import { useSessionRefresh } from './hooks/useSessionRefresh';
import { returnPathFromRouterState } from './services/authService';
import LoginPage from './components/auth/LoginPage';
import WalletBalancesPage from './components/admin/WalletBalancesPage';
import WalletStatementPage from './components/admin/WalletStatementPage';
import WalletTransferPage from './components/admin/WalletTransferPage';
import NonUserWalletsPage from './components/admin/NonUserWalletsPage';
import RepaymentRequestPage from './components/admin/RepaymentRequestPage';
import GlobalCreditIssueRequestsPage from './components/admin/GlobalCreditIssueRequestsPage';

/** Legacy `/wallets/:walletId/credit-issue-requests` → unified list with `wallet_id` query. */
function CreditIssueRequestsLegacyRedirect() {
  const { walletId } = useParams();
  const w = typeof walletId === 'string' ? walletId.trim() : '';
  const to = w
    ? `/wallets/credit-issue-requests?wallet_id=${encodeURIComponent(w)}`
    : '/wallets/credit-issue-requests';
  return <Navigate to={to} replace />;
}

function hasSessionToken() {
  const t = localStorage.getItem('token');
  return typeof t === 'string' && t.trim() !== '';
}

function ProtectedRoute({ children }) {
  const location = useLocation();
  return hasSessionToken() ? (
    children
  ) : (
    <Navigate to="/login" replace state={{ from: location }} />
  );
}

function PublicLoginRoute({ children }) {
  const location = useLocation();
  if (!hasSessionToken()) return children;
  const next = returnPathFromRouterState(location.state?.from) || '/wallets';
  return <Navigate to={next} replace />;
}

function RootRedirect() {
  return <Navigate to={hasSessionToken() ? '/wallets' : '/login'} replace />;
}

function AppRoutes() {
  useSessionRefresh();
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route
        path="/login"
        element={
          <PublicLoginRoute>
            <LoginPage />
          </PublicLoginRoute>
        }
      />
      <Route
        path="/wallets"
        element={
          <ProtectedRoute>
            <WalletBalancesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallets/transfer"
        element={
          <ProtectedRoute>
            <WalletTransferPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallets/credit-issue-requests"
        element={
          <ProtectedRoute>
            <GlobalCreditIssueRequestsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallets/non-user"
        element={
          <ProtectedRoute>
            <NonUserWalletsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallets/:walletId/repayment-request"
        element={
          <ProtectedRoute>
            <RepaymentRequestPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallets/:walletId/credit-issue-requests"
        element={
          <ProtectedRoute>
            <CreditIssueRequestsLegacyRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallets/:walletId/statement"
        element={
          <ProtectedRoute>
            <WalletStatementPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <ToastProvider>
        <div className="min-h-screen bg-gradient-to-b from-slate-100/90 via-slate-50 to-slate-100">
          <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
            <AppRoutes />
          </main>
        </div>
      </ToastProvider>
    </Router>
  );
}

export default App;
